import React, { useEffect, useState } from "react";
import client from "../client/Client";

interface VenueData {
  venueName?: string;
  venueAddress?: string;
  venueLatitude?: string;
  venueLongitude?: string;
  venueGoogleMapsLink?: string;
  venuePhoto?: {
    asset?: {
      _ref?: string;
    };
  };
}

const Venue = () => {
  const [venueData, setVenueData] = useState<VenueData | null>(null);

  useEffect(() => {
    client
      .fetch(
        `*[_type == "WelcomeParagh"][0]{
          venueName,
          venueAddress,
          venueLatitude,
          venueLongitude,
          venueGoogleMapsLink,
          venuePhoto
        }`
      )
      .then((data) => {
        setVenueData(data);
      })
      .catch((error) => {
        console.error("Error fetching venue data:", error);
      });
  }, []);

  if (!venueData) return null;

  const {
    venueName,
    venueAddress,
    venueLatitude,
    venueLongitude,
    venueGoogleMapsLink,
    venuePhoto,
  } = venueData;

  const hasVenueInfo =
    venueName ||
    venueAddress ||
    venuePhoto?.asset?._ref ||
    (venueLatitude && venueLongitude);

  if (!hasVenueInfo) return null;

  const venueImageUrl = venuePhoto?.asset?._ref
    ? `https://cdn.sanity.io/images/79vgbaej/production/${
        venuePhoto.asset._ref.slice(6, -4)
      }.${venuePhoto.asset._ref.slice(-3)}`
    : "";

  const mapEmbedUrl =
    venueLatitude && venueLongitude
      ? `https://www.google.com/maps?q=${venueLatitude},${venueLongitude}&z=16&output=embed`
      : "";

  const googleMapsLink = venueGoogleMapsLink?.trim() || "";

  return (
    <section className="relative py-4 sm:py-6">
      <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mx-auto mb-6 max-w-4xl text-center">
          <h2 className="text-3xl font-bold tracking-tight text-[#050b2c] sm:text-4xl">
            Venue
          </h2>

          {venueName && (
            <h3 className="mt-4 text-2xl font-bold text-[#050b2c] sm:text-3xl">
              {venueName}
            </h3>
          )}

          {venueAddress && googleMapsLink && (
            <a
              href={googleMapsLink}
              target="_blank"
              rel="noopener noreferrer"
              aria-label={`Open ${venueAddress} in Google Maps`}
              className="group mx-auto mt-4 inline-flex max-w-3xl items-center justify-center gap-2 rounded-full bg-red-600 px-5 py-3 text-center text-xs font-bold uppercase tracking-wide text-white shadow-md shadow-red-100 transition-all duration-300 hover:-translate-y-0.5 hover:bg-red-700 hover:shadow-lg focus:outline-none focus:ring-2 focus:ring-red-400 focus:ring-offset-2 sm:text-sm"
            >
              <svg
                className="h-4 w-4 shrink-0 transition-transform duration-300 group-hover:-translate-y-0.5"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                strokeWidth={2.2}
                aria-hidden="true"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M12 21s7-5.1 7-11a7 7 0 10-14 0c0 5.9 7 11 7 11z"
                />
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M12 10.5h.01"
                />
              </svg>

              <span>{venueAddress}</span>

              <svg
                className="h-4 w-4 shrink-0 transition-transform duration-300 group-hover:translate-x-0.5"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                strokeWidth={2.2}
                aria-hidden="true"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M7 17L17 7"
                />
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M9 7h8v8"
                />
              </svg>
            </a>
          )}

          {venueAddress && !googleMapsLink && (
            <p className="mx-auto mt-4 inline-flex max-w-3xl items-center justify-center rounded-full bg-red-600 px-5 py-3 text-center text-xs font-bold uppercase tracking-wide text-white shadow-md shadow-red-100 sm:text-sm">
              {venueAddress}
            </p>
          )}
        </div>

        {/* Venue image and map */}
        <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
          {venueImageUrl && (
            <div className="group overflow-hidden rounded-2xl border border-gray-100 bg-white shadow-xl shadow-gray-200/70">
              <img
                src={venueImageUrl}
                alt={venueName || "Conference venue"}
                className="h-[260px] w-full object-cover transition duration-500 group-hover:scale-105 sm:h-[360px]"
              />
            </div>
          )}

          {mapEmbedUrl && (
            <div className="overflow-hidden rounded-2xl border border-gray-100 bg-white shadow-xl shadow-gray-200/70">
              <iframe
                title={`${venueName || "Conference venue"} map`}
                src={mapEmbedUrl}
                className="h-[260px] w-full border-0 sm:h-[360px]"
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                allowFullScreen
              />
            </div>
          )}
        </div>
      </div>
    </section>
  );
};

export default Venue;